import { captureException } from "@sentry/react";
import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { CLOUDINARY_INTEL_BASE_URL, ERROR_MSG } from "../../../utils/enum";
import { getApiUrl } from "@utils/baseUrl";
import axios from "axios";

const getProductAISummary = async ({ keyword, fromDate, toDate }) => {
  const payload = {
    search: keyword || "",
    fromDate: fromDate || "",
    toDate: toDate || "",
    is_html: true,
    dataSource: [],
  };

  const { data } = await axios.post(getApiUrl("analytics/AnalyticsSummary"), payload);
  return data;
};

const sanitizeHtml = (html) =>
  String(html || "")
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/\s+on\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\s+on\w+\s*=\s*'[^']*'/gi, "");

const buildKeyword = (searchedState) => {
  if (!searchedState?.search?.length) return "";
  return searchedState.search.map((s) => s.label).join(", ");
};

const ProductAISummary = ({ searchedState, defaultDateRange }) => {
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(false);

  const keyword = buildKeyword(searchedState);

  const fetchSummary = useCallback(async () => {
    if (!searchedState) return;
    setLoading(true);
    try {
      const fromDate = defaultDateRange?.startDate || "";
      const toDate = defaultDateRange?.endDate || "";
      const dataSource = [];
      const res = await getProductAISummary({ keyword, fromDate, toDate, dataSource });
      if (res?.status === "success") {
        setSections(res.sections || null);
      } else {
        setSections(null);
      }
    } catch (err) {
      captureException(err);
      toast.error(ERROR_MSG);
      setSections(null);
    } finally {
      setLoading(false);
    }
  }, [keyword, defaultDateRange, searchedState]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const formatHeading = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="tw-pt-4">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <div className="tw-text-sm tw-font-semibold tw-flex tw-items-center tw-gap-1">
          <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1761888982/Sanctions/sparkle_3_hv5h4n.svg`} alt="AI Summary" className="tw-w-5 tw-h-5" />
          AI Summary{keyword ? ` for ${keyword}` : ""}
        </div>
      </div>

      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-6 tw-text-sm tw-text-gray-800 tw-min-h-[160px]">
        {loading && <div className="tw-text-gray-500">Generating summary...</div>}

        {!loading && sections && (
          <div>
            {Object.entries(sections)
              .filter(([, html]) => html !== "")
              .map(([key, html]) => (
                <div key={key} className="tw-mb-4">
                  <div className="tw-font-semibold tw-text-base tw-underline tw-mb-2">{formatHeading(key)}</div>
                  <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
                </div>
              ))}
          </div>
        )}

        {!loading && !sections && <div className="tw-text-gray-500">No summary available.</div>}
      </div>
    </div>
  );
};

export default ProductAISummary;
